// The guided tour of the Surveys page, for the three kinds of people who use it.
//
// PMs and the PMO (holders of surveys:review) request surveys, import ones
// done on paper, review what comes back and decide whether an opportunity
// needs one at all. The bid team requests and imports surveys from a bid but
// does not review them. Engineers and anyone else sent to do a survey find
// theirs here and open it; the survey form has its own tour
// (surveyFormTour.js) for filling one in.
//
// Steps are gated on `canReview`, the same fact the server sends the page as
// can_review, and `canRequest`, whether the person can open the screens a
// survey is requested from (the Opportunity Tracker or the Pipeline Tracker).
// Anybody holding Surveys may request one, so this asks where they work
// rather than what they may do.
//
// Steps point at [data-tour] anchors on SurveysPage, in the order the page
// reads. The steps describing the opportunity's Surveys tab and the review
// buttons are on other screens, so they show without a spotlight.

export const SURVEYS_TOUR_ID = 'surveys';

const pm = (c) => !!c.canReview;
const requester = (c) => !!c.canRequest && !c.canReview;
const requests = (c) => !!c.canRequest || !!c.canReview;
const surveyor = (c) => !c.canRequest && !c.canReview;
const notPm = (c) => !c.canReview;

export const surveysTourSteps = [
  {
    title: 'Welcome to Surveys',
    target: '[data-tour="page-header"]',
    body: [
      'Physical work such as desk moves, desk installs and meeting rooms is surveyed before it is priced. Each survey is one room or area, on its opportunity, or on its bid before the win.',
      'There are three kinds. A room survey is the nine questions of the AV / IT Site Survey. A desk standardisation records every desk against the agreed standards. A desk move records each person or desk that moves, the ports at both ends and move day. They carry a Desks or Desk move tag in the list.',
      'Use Next (or the arrow keys) to walk through, or Skip. It will not pop up again, and the ? button replays it.',
    ],
  },
  {
    title: 'Your part, as a PM',
    when: pm,
    body: [
      'You request the survey, send someone to do it, review what comes back, and decide whether the opportunity needs one before it is issued. The engineer fills it in, on a phone or on the paper form.',
      'A survey moves from Requested to Booked (when the engineer and the visit date are set), In progress (once answers are recorded), Submitted (waiting for you) and Reviewed.',
    ],
  },
  {
    title: 'Your part, if you request surveys',
    when: requester,
    body: [
      'You request a survey from the bid or the opportunity and choose who carries it out. They fill it in and submit it, and a PM or the PMO reviews it.',
      'A survey moves from Requested to Booked (when the engineer and the visit date are set), In progress (once answers are recorded), Submitted (waiting for review) and Reviewed.',
    ],
  },
  {
    title: 'Your part, if you carry out surveys',
    when: surveyor,
    body: [
      'A PM or the bid team requests a survey and names you as the engineer. You visit the site, fill the survey in, and submit it for the PM to review. If the PM sends it back, it returns to you to finish.',
    ],
  },
  {
    title: 'Find your surveys',
    target: '[data-tour="filters"]',
    when: notPm,
    body: [
      'Tick Mine to see only the surveys where you are the engineer. Open shows everything not yet reviewed or cancelled.',
      'Search looks at the reference, the room, the opportunity, the bid and the client.',
    ],
  },
  {
    title: 'What is waiting for you',
    target: '[data-tour="filters"]',
    when: pm,
    body: [
      'Submitted is what waits for review. Open is everything not yet reviewed or cancelled, and Mine is the surveys where you are the engineer yourself.',
      'Search looks at the reference, the room, the opportunity, the bid and the client.',
    ],
  },
  {
    title: 'Open a survey',
    target: '[data-tour="table"]',
    body: [
      'Click a row to open the survey. The form has its own short tour the first time you open one, section by section.',
    ],
  },
  {
    title: 'Request a survey',
    when: requests,
    body: [
      'Surveys are requested from the Surveys tab of the opportunity on the Opportunity Tracker, or from the Surveys tab of a bid before the win. A bid\'s surveys appear on the opportunity the win raises.',
      'Choose Room, Desk standardisation or Desk move, name the room or area (one survey per room), choose who carries it out and the visit date, and tick Paper only for a site where phones and cameras are not allowed.',
      'For a desk move, add the client\'s move list (an Excel file, in their own layout). The dialog shows every move it read before you raise the survey, and the survey starts with those moves.',
      'Every survey form comes from Ops. Open the survey and use Download form for the copy stamped with its reference, and Upload completed form to read the completed copy back into it.',
    ],
  },
  {
    title: 'Blank forms, and importing one',
    target: '[data-tour="blank-forms"]',
    when: requests,
    body: [
      'Blank forms gives the current room, desk standardisation and desk move forms, for a survey done before it was raised in Ops.',
      'Import a completed survey, on the opportunity or bid\'s Surveys tab, reads a completed blank form into a new survey and keeps the file with it. A form downloaded from a survey already in Ops is refused there: upload it on that survey instead.',
      'Anything the file did not answer clearly, such as a box not ticked or a number in the wrong place, is listed in the dialog. Check those in the survey before it is reviewed.',
    ],
  },
  {
    title: 'Review what comes back',
    when: pm,
    body: [
      'Open a Submitted survey. The Not standard box lists every answer that changes the price; on a desk standardisation, the Totals section gives the kit to order, desks short of sockets and ports not recorded.',
      'Mark reviewed when it is right; it is then locked as the record. Send back returns it to the engineer to finish. Reopen unlocks a reviewed survey if something changes.',
    ],
  },
  {
    title: 'Does it need a survey?',
    when: pm,
    body: [
      'The opportunity\'s Surveys tab says whether a survey is needed before issue. It is yes for PI/TI work unless you change it.',
      'The Survey column on the Opportunity Tracker shows where each opportunity stands: Needed, to review, or reviewed.',
    ],
  },
];
